import { useState, useEffect, useMemo, useCallback } from 'react'
import useOnCurrentUserLoaded from 'shared/hooks/useOnCurrentUserLoaded/useOnCurrentUserLoaded'
import {
  setExpiringLocalStorage,
  getExpiringLocalStorage,
} from 'baby-ui/utils/expireLocalStorage'
import useFeatureFlag from 'shared/hooks/useFeatureFlag/useFeatureFlag'
import { track, useTracking } from 'lib/analytics'

import { newRegistryPath } from '../../../../../lib/urls'
import { CurrentUser } from '../../../../../global'

import {
  SignUpBannerControl,
  SignUpBannerT1,
  SignUpBannerT2,
} from './treatments'
import { SignUpBannerTreatmentProps } from './treatments/types'

export interface SignUpBannerProps {
  // Don't wait for an actual scroll to set "scrolled" state
  // Used for tests to avoid mocking scroll behavior
  showImmediately?: boolean
}

const SignUpBanner = ({ showImmediately }: SignUpBannerProps) => {
  const tracker = useTracking()
  const { flagValue: bannerTreatment } = useFeatureFlag(
    '24f_ex_signup_banner',
    'Control',
    { logUsage: true }
  )

  const [scrolled, setScrolled] = useState(showImmediately || false)

  const dismissalKey = 'dismissibleBanner-guidesSignupBar'

  const persistDismissal = useCallback(() => {
    // Persist dismissal state
    setExpiringLocalStorage(dismissalKey, 'dismissed', 3600)
  }, [dismissalKey])

  // Has the banner been dismissed already?
  // Memoized on mount so the banner does not suddenly re-appear after TTL
  // if this component happens to be re-rendered without a page navigation
  const dismissedWithinTTL = useMemo(
    () => dismissalKey && getExpiringLocalStorage(dismissalKey) === 'dismissed',
    []
  )

  // Extend dismissal period after render if previously dismissed and within TTL
  useEffect(() => {
    if (dismissedWithinTTL) {
      persistDismissal()
    }
  }, [dismissedWithinTTL, persistDismissal])

  // So async loading doesn't affect LCP
  const onScroll = () => {
    setScrolled(true)
    window.removeEventListener('scroll', onScroll)
  }
  useOnCurrentUserLoaded((currentUser?: CurrentUser) => {
    // Only shown to anonymous users
    if (!currentUser) window.addEventListener('scroll', onScroll)
  })

  const trackDismissal = useCallback(() => {
    persistDismissal()

    tracker.trackEvent({
      event: track.signupBannerDismissed,
      bannerTreatment,
    })
  }, [bannerTreatment, tracker, persistDismissal])

  const trackDisplay = useCallback(() => {
    tracker.trackEvent({
      event: track.signupBannerDisplayed,
      bannerTreatment,
    })
  }, [bannerTreatment, tracker])

  const trackClick = useCallback(
    (cta) => {
      tracker.trackEvent({
        event: track.signupBannerClicked,
        eventCta: cta,
        bannerTreatment,
      })
    },
    [bannerTreatment, tracker]
  )

  const bannerProps: SignUpBannerTreatmentProps = {
    ctaPath: newRegistryPath,
    previouslyDismissed: dismissedWithinTTL,
    scrolled,
    onClick: trackClick,
    onDismissal: trackDismissal,
    onDisplay: trackDisplay,
  }

  if (bannerTreatment === 'T1') {
    return <SignUpBannerT1 {...bannerProps} />
  }

  if (bannerTreatment === 'T2') {
    return <SignUpBannerT2 {...bannerProps} />
  }

  return <SignUpBannerControl {...bannerProps} />
}

export default SignUpBanner
